// Authentication service — the only auth API the rest of the app uses.
//
//   await auth.init()   → restore the session after a page load (Supabase: incl. email links)
//   auth.current()      → session or null           auth.user() → { id, name, role, email? } or null
//   auth.isAdmin()      → true only when the account's role (from the database) is admin
//   auth.login(creds, { ttlMs? })   → provider.login → session stored (12 h, or 30 days) → listeners
//   auth.signup(details, { ttlMs? }) → { session } | { pending, email } (email confirmation)
//   auth.requestPasswordReset(email) · auth.resendConfirmation(email) · auth.updatePassword(pw)
//   auth.logout() · auth.onChange(fn)
//   auth.accounts       → true with real accounts (Supabase), false with the development mock
//
// Provider: Supabase when VITE_SUPABASE_URL + key are set (src/auth/supabaseClient.js),
// otherwise the development mock. Swap providers without touching callers: createAuthService({ provider }).
import { createSessionStore, ROLES, REMEMBER_TTL_MS } from './session.js';
import { mockAuthProvider, AuthError } from './mockAuthProvider.js';
import { createHttpAuthProvider } from './httpAuthProvider.js';
import { getSupabase, supabaseConfigured, setRemember } from './supabaseClient.js';
import { createSupabaseAuthProvider } from './supabaseAuthProvider.js';

const unsupported = () => { throw new AuthError('unsupported', 'This is not available in the development build.'); };

export function createAuthService({ provider = mockAuthProvider, sessions = createSessionStore() } = {}) {
  const listeners = new Set();
  const emit = () => { for (const f of listeners) f(api.current()); };
  let ending = false;
  const endExpired = () => { // RouteZero's expiry passed: end the provider's own session too
    if (ending || !provider.accounts) return;
    ending = true;
    Promise.resolve(provider.logout()).finally(() => { ending = false; });
  };
  // the mirror never holds real Supabase tokens (supabase-js keeps those itself)
  const start = (user, ttlMs, token = 'account') => sessions.set({ user, token, provider: provider.name, ...(ttlMs > 0 ? { ttlMs } : {}) });

  const api = {
    provider,
    accounts: !!provider.accounts,
    /** Set by init() when the page was opened from an email link: 'confirmed' | 'reset' | null. */
    linkResult: null,
    linkError: null,
    current() {
      const s = sessions.get();
      if (!s && sessions.peek()) { sessions.clear(); endExpired(); }
      return s;
    },
    user: () => sessions.get()?.user || null,
    isAuthenticated: () => !!sessions.get(),
    role: () => sessions.get()?.user.role || null,
    hasRole: (r) => sessions.get()?.user.role === r,
    isAdmin: () => sessions.get()?.user.role === ROLES.ADMIN,

    async init({ search = typeof location !== 'undefined' ? location.search : '' } = {}) {
      const marker = new URLSearchParams(search).get('rz');
      if (!provider.restore) return api.current();
      const before = sessions.peek();
      const { user, linkError } = await provider.restore();
      api.linkError = linkError;
      if (!user) {
        if (before) { sessions.clear(); emit(); }
      } else if (before && before.user.id === user.id) {
        if (Date.now() > before.expiresAt) { sessions.clear(); await provider.logout(); emit(); }
        else { sessions.updateUser(user); }
      } else {
        // a fresh session from an email link (confirm address / reset password), or a different account
        start(user, marker === 'reset' ? 0 : undefined);
        emit();
      }
      api.linkResult = marker === 'confirmed' || marker === 'reset' ? marker : null;
      if (provider.onExternalChange && !api._unsub) {
        api._unsub = provider.onExternalChange((event) => {
          if (event === 'SIGNED_OUT' && sessions.peek()) setTimeout(() => { sessions.clear(); emit(); }, 0);
        });
      }
      return api.current();
    },

    /** @param {object} credentials  @param {{ ttlMs?: number }} [opts] "Keep me signed in" → a longer session */
    async login(credentials, { ttlMs } = {}) {
      setRemember(ttlMs >= REMEMBER_TTL_MS);
      const { user, token } = await provider.login(credentials);
      const s = start(user, ttlMs, provider.accounts ? 'account' : token);
      emit();
      return s;
    },
    async signup(details, { ttlMs } = {}) {
      setRemember(ttlMs >= REMEMBER_TTL_MS);
      if (!provider.signup) return { session: await api.login(details, { ttlMs }) };
      const r = await provider.signup(details);
      if (r.pending) return { pending: true, email: r.email };
      const s = start(r.user, ttlMs);
      emit();
      return { session: s };
    },
    resendConfirmation: (email) => (provider.resendConfirmation || unsupported)(email),
    requestPasswordReset: (email) => (provider.requestPasswordReset || unsupported)(email),
    async updatePassword(pw) { await (provider.updatePassword || unsupported)(pw); },
    /** Re-read name/role from the account database (Supabase only). */
    async refresh() {
      if (!provider.refreshUser || !sessions.get()) return api.current();
      const u = await provider.refreshUser();
      if (u) { sessions.updateUser(u); emit(); }
      return api.current();
    },
    async logout() {
      const s = sessions.peek();
      sessions.clear();
      try { await provider.logout(s); } finally { emit(); }
    },
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  };
  return api;
}

/** App-wide instance: Supabase when configured, else the development mock (or the legacy HTTP stub). */
const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
function pickProvider() {
  if (supabaseConfigured()) return createSupabaseAuthProvider({ client: getSupabase() });
  if (env.VITE_AUTH_PROVIDER === 'http') return createHttpAuthProvider({ baseUrl: env.VITE_API_BASE || '' });
  return mockAuthProvider;
}
export const auth = createAuthService({ provider: pickProvider() });
export { ROLES };
